import { useEffect, useRef, useState } from 'react';
import PageContainer from '../../../components/container/PageContainer';
import {
  MANHOUR_HEADER,
  MANHOUR_MODAL_FIELDS,
} from '../../../constants/manhour';
import {
  EmptyManHoursGetResponse,
  ManHoursGetResponse,
} from '../../../types/manhour/ManHourGetResponse';
import { ManHourCsvPreviewItem } from '../../../types/manhour/ManHourCsvPreviewItem';
import { getManHourList, exportManHoursCsv } from '../../../services/api';
import { Col, Row } from 'react-bootstrap';
import RenderManHourRow from './RenderManHourRow';
import TablePagination from '../../../components/table/pagination/TablePagination';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  DefaultManHourGetQueryParams,
  ManHourGetQueryParams,
} from '../../../types/manhour/ManHourGetQueryParams';
import { SortOrder } from '../../../types/sort/sort';
import {
  MANHOUR_DEFAULT_SORT_ORDER,
  MANHOUR_DEFAULT_SORT_COLUMN,
} from '../../../constants/sort';
import CsvModal from '../../../components/modal/CsvModal';
import ProjectSearch from '../../../components/search/ProjectSearch';
import NameSearch from '../../../components/search/NameSearch';
import YmPicker from '../../../components/picker/YmPicker';
import { format } from 'date-fns';
import { formatYmDisplay } from '../../../utils/formatYmDisplay';
import SearchResetButtons from '../../../components/button/SearchResetButtons';
import CsvExportButton from '../../../components/button/CsvExportButton';
import ReadOnlyTable from '../../../components/table/ReadOnlyTable';
import { saveBlobAsFile } from '../../../utils/saveBlobAsFile';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/ManHourHeader.css';

const ManHour = () => {
  const [getQueryParams, setGetQueryParams] = useState<ManHourGetQueryParams>(
    DefaultManHourGetQueryParams
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    MANHOUR_DEFAULT_SORT_ORDER
  );
  const [activeSortColumn, setActiveSortColumn] = useState<string>(
    MANHOUR_DEFAULT_SORT_COLUMN
  );
  const [projectLabel, setProjectLabel] = useState<string | null>(null);
  const [employeeLabel, setEmployeeLabel] = useState<string | null>(null);
  const [csvItem, setCsvItem] = useState<ManHourCsvPreviewItem | null>(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
  const [controlsKey, setControlsKey] = useState(0);
  const [resetSignal, setResetSignal] = useState(0); // 検索リセット用のシグナル
  const queryClient = useQueryClient();
  const fmtYM = (d: Date | null | undefined) => (d ? format(d, 'yyyy-MM') : '');

  // 表の横スクロール要素（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null); // 高さ計算用
  const footerRef = useRef<HTMLDivElement>(null); // 高さ計算用

  const { mutateAsync: exportCsv } = useMutation({
    mutationKey: ['exportManHoursCsv'],
    mutationFn: () => exportManHoursCsv(getQueryParams),
  });

  // 検索のクエリパラメーターをセット
  const setQuery = (
    patch: {
      selectedMonth?: Date | null;
    } & Partial<
      Pick<ManHourGetQueryParams, 'projectId' | 'employeeId' | 'month'>
    >
  ) => {
    setGetQueryParams((prev) => {
      const next = {
        ...prev,
        ...patch,
        ...(patch.selectedMonth !== undefined
          ? { month: fmtYM(patch.selectedMonth) }
          : {}),
      } as ManHourGetQueryParams;

      return next;
    });
  };

  // 一覧取得
  const {
    data: manHourGetResponse,
    isError,
    error,
  } = useQuery<ManHoursGetResponse>({
    queryKey: ['manHourList'],
    queryFn: () => getManHourList(getQueryParams),
    initialData: EmptyManHoursGetResponse,
  });

  // ページ数、ソート順、ソートカラムが変わったら一覧を再取得
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['manHourList'],
    });
  }, [getQueryParams.page, getQueryParams.sortKey, getQueryParams.sortOrder]);

  // 一覧取得失敗時のアラート
  useEffect(() => {
    if (isError) {
      alert(error as any);
    }
  }, [isError]);

  // ソートの状態を管理
  const handleSort = (sortKey: string) => {
    if (sortKey === activeSortColumn) {
      // 選択中のカラムが現在のアクティブなカラムと同じ場合、ソート順を切り替える
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // 新しいカラムが選択された場合、アクティブなカラムを降順で更新
      setActiveSortColumn(sortKey);
      setSortOrder('desc');
    }
  };

  // ソートの状態が変わったときにクエリパラメータを更新
  useEffect(() => {
    setGetQueryParams((prev) => ({
      ...prev,
      sortOrder: sortOrder,
      sortKey: activeSortColumn,
    }));
  }, [sortOrder, activeSortColumn]);

  // CSV出力用のモーダル情報を作成
  const buildCsvItem = (): ManHourCsvPreviewItem => {
    const monthDisp = formatYmDisplay(getQueryParams.month) || '全期間';

    const projectName =
      getQueryParams.projectId != null
        ? (projectLabel ?? '')
        : '全プロジェクト';

    const employeeName =
      getQueryParams.employeeId != null ? (employeeLabel ?? '') : '全社員';

    return { projectName, employeeName, month: monthDisp };
  };

  const onCsvButtonClick = async () => {
    setCsvItem(buildCsvItem());
    setShowCsvModal(true);
  };

  const onCsvModalSubmitButtonClick = async () => {
    try {
      const { blob, filename } = await exportCsv();

      const fallback = 'manhours.csv';
      const resolved =
        filename && filename.trim() ? decodeURIComponent(filename) : fallback;

      const ok = await saveBlobAsFile(blob, resolved);
      if (ok) alert('CSVファイルの保存が完了しました');
    } catch (err: any) {
      let msg = '';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        const json = JSON.parse(text);
        if (json?.message) msg = json.message;
      }
      alert(msg);
    }
  };

  return (
    <PageContainer title="工数管理">
      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-manhour gx-0">
          {/* 左：検索条件（幅は固定で縮めない） */}
          <Col className="header-fields-manhour order-1">
            <NameSearch
              key={`employee-${controlsKey}`}
              setSelectedId={(e: number) => setQuery({ employeeId: e })}
              onSelectedLabel={(label) => setEmployeeLabel(label)}
              resetSignal={resetSignal}
            />
            <ProjectSearch
              key={`project-${controlsKey}`}
              setSelectedId={(p: number) => setQuery({ projectId: p })}
              onSelectedLabel={(label) => setProjectLabel(label)}
              resetSignal={resetSignal}
            />
            <YmPicker
              placeholder="期間を選択"
              selectedMonth={selectedMonth}
              setSelectedMonth={(d) => {
                setSelectedMonth(d);
                setQuery({ selectedMonth: d });
              }}
            />
          </Col>

          {/* 右：ボタン群（検索/リセットは常に横並び、CSVは折り返し対象） */}
          <Col xs="auto" className="header-right-manhour order-2">
            <div className="actions-group-manhour">
              <div className="actions-searchreset-manhour">
                <SearchResetButtons
                  onSearch={async () => {
                    // 検索ボタン押下時はページを1に戻す
                    setGetQueryParams((prev) => ({ ...prev, page: 1 }));
                    const paramsForSearch = {
                      ...getQueryParams,
                      page: 1,
                    };
                    const data = await queryClient.fetchQuery({
                      queryKey: ['manHourList'],
                      queryFn: () => getManHourList(paramsForSearch),
                    });
                    if (!data.items || data.items.length === 0) {
                      alert('該当データが存在しません');
                    }
                  }}
                  onReset={() => {
                    setSelectedMonth(null);
                    setProjectLabel(null);
                    setEmployeeLabel(null);
                    setGetQueryParams(DefaultManHourGetQueryParams);
                    setControlsKey((k) => k + 1);
                    setResetSignal((s) => s + 1);
                    queryClient.fetchQuery({
                      queryKey: ['manHourList'],
                      queryFn: () =>
                        getManHourList(DefaultManHourGetQueryParams),
                    });
                  }}
                />
              </div>
              <div className="actions-extra-manhour">
                <CsvExportButton
                  onClick={onCsvButtonClick}
                  buttonLabel="CSV出力"
                />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <ReadOnlyTable
        headers={MANHOUR_HEADER}
        items={manHourGetResponse.items}
        renderItem={(item) => <RenderManHourRow item={item} />}
        onSort={handleSort}
        sortOrder={sortOrder}
        activeSortColumn={activeSortColumn}
        actionable={false}
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
      />

      <TablePagination
        totalPages={manHourGetResponse.totalPages}
        onPageChange={(page) =>
          setGetQueryParams((prev) => ({ ...prev, page }))
        }
        ref={footerRef}
      />

      {showCsvModal && csvItem && (
        <CsvModal<ManHourCsvPreviewItem>
          show={showCsvModal}
          item={csvItem}
          fields={MANHOUR_MODAL_FIELDS}
          saveButtonLabel="OK"
          onClose={() => {
            setShowCsvModal(false);
            setCsvItem(null);
          }}
          onConfirm={async () => {
            await onCsvModalSubmitButtonClick();
            setShowCsvModal(false);
            setCsvItem(null);
          }}
        />
      )}
    </PageContainer>
  );
};

export default ManHour;
